import axios from "axios";
import { getToken } from "../Apimanager/Networking";
import { store } from "../redux/store";
import { toggleNorthwellError } from "../redux/actions/globals.action";
import { ShowAlert } from "../common/alert";
import { getOnboardingBaseUrl, handleApiError } from "./apiUtils";
import { isCognitoAuth } from "../auth/region";
import { forceReauth } from "../auth/session";

const baseURL = getOnboardingBaseUrl();

export const checkHealth = async () => {
   try {
      const response = await axios.get("/health");
      return response.data;
   } catch (error) {
      handleApiError(error, "Health check failed");
   }
};

export const registerProvider = async ({ email, name, referralCode, signUpMethod }) => {
   try {
      const response = await axios.post(baseURL + "/providers", {
         email,
         name,
         referralCode,
         signUpMethod,
      });
      return response.data;
   } catch (error) {
      const isNorthwellUserExist =
         error?.data?.message ===
         "You don't currently have access to this app. Please contact Northwell support through MyTech to request access.";
      if (isNorthwellUserExist) {
         localStorage.setItem("provider", "northwell");
         if (email?.toLowerCase()?.includes("@northwell.edu")) {
            store.dispatch(toggleNorthwellError());
         } else {
            ShowAlert(error.data?.message, "error");
         }
      } else {
         ShowAlert(error.data?.message, "error");
      }

      throw error;
   }
};

export const registerProviderWithGoogle = async ({ token, referralCode }) => {
   try {
      const response = await axios.post(baseURL + "/providers", {
         signUpMethod: "social",
         token,
         referralCode,
      });
      return response.data;
   } catch (error) {
      throw error;
   }
};

export const updateProvider = async ({ specialities }) => {
   const userId = store.getState().auth?.userCredentials?.user?.id;

   const token = await getToken();
   try {
      const response = await axios.patch(
         baseURL + "/providers",
         { userId, specialities },
         {
            headers: {
               Authorization: `Bearer ${token}`,
            },
         }
      );
      return { response: response.data, userData: store.getState().auth.userCredentials };
   } catch (error) {
      handleApiError(error, "Provider update failed");
   }
};

export const loginProvider = async ({ email, signInMethod }) => {
   try {
      const response = await axios.post(baseURL + "/login", {
         email,
         signInMethod,
      });
      return response.data;
   } catch (error) {
      handleApiError(error, "Login failed");
   }
};

export const checkLogin = async ({ email, signInMethod, unsetLoader }) => {
   try {
      const response = await axios.post(baseURL + "/login", {
         email,
         signInMethod,
      });
      return response.data;
   } catch (error) {
      const isNorthwellUserExist =
         error?.data?.message ===
         "You don't currently have access to this app. Please contact Northwell support through MyTech to request access.";
      if (isNorthwellUserExist) {
         localStorage.setItem("provider", "northwell");
         if (email?.toLowerCase()?.includes("@northwell.edu")) {
            store.dispatch(toggleNorthwellError());
            unsetLoader();
         } else {
            ShowAlert(error.data?.message, "error");
         }
      }
      else {
         ShowAlert(error?.data?.message || "Login check failed", "error");
      }
      throw error;
   }
};

export const verifyOTP = async ({ key, otp }) => {
   try {
      const response = await axios.post(baseURL + "/login/verify", {
         key,
         otp,
         authMethod: "otp",
      });
      return response.data;
   } catch (error) {
      handleApiError(error, "OTP verification failed");
   }
};

export const resendOTPVerify = async ({ key }) => {
   try {
      const response = await axios.post(baseURL + "/login/verify", {
         key,
         authMethod: "resendOtp",
      });
      return response.data;
   } catch (error) {
      throw error;
   }
};

export const verifyPassword = async ({ key, password, authMethod }) => {
   try {
      const response = await axios.post(baseURL + "/login/verify", {
         key,
         password,
         authMethod,
      });
      return response.data;
   } catch (error) {
      handleApiError(error, "Password verification failed");
   }
};

export const verifyToken = async ({ firebaseToken }) => {
   try {
      const response = await axios.post(baseURL + "/login/verify", {
         firebaseToken,
         authMethod: "token",
      });
      return response.data;
   } catch (error) {
      const status = error?.status || error?.response?.status;
      if (status === 400 || status === 401 || status === 403) {
         forceReauth();
         return;
      }
      handleApiError(error, "Token verification failed");
   }
};

export const authenticateProvider = async ({ token, signInMethod = "token" }) => {
   try {
      const response = await axios.post(baseURL + "/login", {
         token,
         signInMethod,
      });
      return response.data;
   } catch (error) {
      throw error;
   }
};

export const customToken = async ({ sessionContext }) => {
   try {
      const response = await axios.post(baseURL + "/providers/custom-token ", {
         sessionContext,
      });
      return response.data;
   } catch (error) {
      handleApiError(error, "Password verification failed");
   }
};

export const epicSaLaunch = async ({ key, code }) => {
   try {
      const response = await axios.post(baseURL + "/providers/epicsa-launch ", {
         key,
         code,
      });
      return response.data;
   } catch (error) {
      handleApiError(error, "Password verification failed");
   }
};

export const SendOTPToPhone = async ({ mobileNo, code }) => {
   const token = await getToken();
   try {
      const endPoint = isCognitoAuth() ? "/eu/send-otp-to-verification" : "/send-otp-to-verification"
      const response = await axios.post(
         baseURL + endPoint,
         { code, mobileNo },
         {
            headers: {
               Authorization: `Bearer ${token}`,
            },
         }
      );
      return response.data;
   } catch (error) {
      handleApiError(error, "Password verification failed");
   }
};

export const verifyOTPMobileNumber = async ({ otp,
   key }) => {
   const token = await getToken();
   try {
      const endPoint = isCognitoAuth() ? "/eu/verify-otp-mobile" : "/verify-otp-mobile"
      const response = await axios.post(
         baseURL + endPoint,
         { otp, key },
         {
            headers: {
               Authorization: `Bearer ${token}`,
            },
         }
      );
      return response.data;
   } catch (error) {
      handleApiError(error, "Password verification failed");
   }
};